import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../redux/hooks';
import FilterDropdown, { FilterOption } from '../../../components/custom-input/FilterDropdown';
import { Animal } from '../../../types/interfaces';
import { getOnFarmAccountAnimals } from '../../../services/onFarmInventory.service';
import { showToast } from '../../../services/toast.service';
import { toastMessages } from '../../../constants/errorMessages';
import { ROUTE_PATHS } from '../../../constants/routePaths';
import { ANIMAL_CONSTANTS } from '../../../constants/common';

type OnFarmAnimalSelectorProps = {
  animalId?: string;
  actionPath: string;
};

const OnFarmAnimalSelector: FC<OnFarmAnimalSelectorProps> = ({ animalId, actionPath }): JSX.Element => {
  const navigate = useNavigate();
  const activeAccount = useAppSelector(state => state.activeAccount);

  const [animalOptions, setAnimalOptions] = useState<FilterOption<Animal>[]>([]);
  const [selectedAnimal, setSelectedAnimal] = useState<FilterOption<Animal>>();

  useEffect(() => {
    const createFilterOptions = (animals: Animal[]) => {
      return animals.map(
        (animal: Animal): FilterOption<Animal> => ({
          name: `${animal.code}-${animal.name}`,
          value: animal,
        }),
      );
    };

    const getOwnedAnimals = async () => {
      setAnimalOptions([]);
      if (!activeAccount?.accountId) return;

      try {
        const { data } = await getOnFarmAccountAnimals();
        const animals = data.result;
        const options = createFilterOptions(animals);
        const matchedAnimal = animals.find(animal => animal.animalId === Number(animalId));

        if (matchedAnimal) {
          setSelectedAnimal({ name: `${matchedAnimal.code}-${matchedAnimal.name}`, value: matchedAnimal });
        } else {
          setSelectedAnimal(undefined);
        }
        setAnimalOptions(options);
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    };

    getOwnedAnimals();
  }, [activeAccount?.accountId, animalId]);

  const onAnimalChange = (option: FilterOption<Animal>) => {
    setSelectedAnimal(option);
    if (option?.value?.animalId) {
      navigate(`${ROUTE_PATHS.APP_ON_FARM_INVENTORY}/${option.value.animalId}/${actionPath}`);
    }
  };

  return (
    <div className="form-row">
      <label>{ANIMAL_CONSTANTS.ANIMAL}:</label>
      <div className="input-container">
        <FilterDropdown
          options={animalOptions}
          value={selectedAnimal ?? { name: '', value: undefined }}
          disabled={animalOptions.length === 0}
          onChange={onAnimalChange}
        />
      </div>
    </div>
  );
};

export default OnFarmAnimalSelector;
